import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Wallet, Layers, FileWarning, Database, Loader2, ShieldCheck,
  ChevronDown, Microscope, Clock, type LucideIcon,
} from 'lucide-react';
import { walletIntelApi } from '@/api/walletIntel';
import { CATEGORY_LABEL, CATEGORY_COLOR, SOURCE_LABEL } from '@/types/walletIntel';
import { WalletIntelSubNav } from '@/components/wallet-intel/SubNav';
import { CategoryExplorerModal } from '@/components/wallet-intel/CategoryExplorerModal';

function relativeTime(iso?: string): string {
  if (!iso || iso === 'unknown') return '—';
  const delta = Date.now() - new Date(iso).getTime();
  const d = Math.floor(delta / (1000 * 60 * 60 * 24));
  if (d < 1) return 'today';
  if (d === 1) return '1 day ago';
  if (d < 30) return `${d} days ago`;
  const m = Math.floor(d / 30);
  return `${m} month${m > 1 ? 's' : ''} ago`;
}

// Smoothly counts up to `value` on mount/change — small, dependency-free.
function CountUp({ value, duration = 900 }: { value: number; duration?: number }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf: number;
    const start = performance.now();
    const from = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (value - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{display.toLocaleString()}</>;
}

function StatCard({ icon: Icon, label, value, color, delay = 0 }: {
  icon: LucideIcon;
  label: string; value: number | string; color: string; delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, boxShadow: `0 8px 24px -8px ${color}40` }}
      transition={{ duration: 0.3, delay }}
      style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 14, padding: '18px 20px' }}
      className="flex items-center gap-4"
    >
      <div style={{ width: 46, height: 46, borderRadius: 12, background: `${color}18`, border: `1px solid ${color}30` }}
        className="flex items-center justify-center flex-shrink-0">
        <Icon size={21} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 26, lineHeight: 1.1 }}>
          {typeof value === 'number' ? <CountUp value={value} /> : value}
        </div>
        <div style={{ color: '#7C8CA6', fontSize: 11, marginTop: 3, letterSpacing: '0.04em', fontWeight: 600 }}>{label}</div>
      </div>
    </motion.div>
  );
}

function QuickAction({ icon: Icon, title, desc, onClick, color }: {
  icon: LucideIcon;
  title: string; desc: string; onClick: () => void; color: string;
}) {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ y: -3, borderColor: `${color}60` }}
      whileTap={{ scale: 0.98 }}
      className="text-left"
      style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 14, padding: '18px 20px' }}
    >
      <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={17} style={{ color }} />
      </div>
      <p style={{ color: '#F2F6FC', fontWeight: 700, fontSize: 14, marginTop: 12 }}>{title}</p>
      <p style={{ color: '#7C8CA6', fontSize: 12, marginTop: 4 }}>{desc}</p>
    </motion.button>
  );
}

export default function WalletIntelDashboard() {
  const navigate = useNavigate();
  const [quickAddress, setQuickAddress] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showSourceDetail, setShowSourceDetail] = useState(false);

  const statsQuery = useQuery({
    queryKey: ['wallet-intel-stats'],
    queryFn: () => walletIntelApi.stats(),
    staleTime: 60_000,
  });

  const stats = statsQuery.data;
  const topCategories = stats
    ? Object.entries(stats.by_category).sort((a, b) => b[1] - a[1]).slice(0, 6)
    : [];
  const maxCategory = topCategories[0]?.[1] ?? 1;
  const sourceCount = stats ? Object.keys(stats.by_source).length : 0;
  const mostRecentUpdate = stats
    ? Object.values(stats.freshness_by_source).filter((v) => v !== 'unknown').sort().reverse()[0]
    : undefined;

  function goLookup() {
    if (!quickAddress.trim()) return;
    navigate(`/app/wallet-intel/lookup?address=${encodeURIComponent(quickAddress.trim())}`);
  }

  return (
    <div style={{ background: '#050709', minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WalletIntelSubNav />
      {selectedCategory && (
        <CategoryExplorerModal category={selectedCategory} onClose={() => setSelectedCategory(null)} />
      )}

      {/* Header */}
      <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 14 }} className="px-7 py-5 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 20, letterSpacing: '0.02em' }}>
              Wallet Intelligence
            </h1>
            <p style={{ color: '#7C8CA6', fontSize: 12.5, marginTop: 3 }}>
              Blockchain wallet risk screening · indirect exposure · clustering · SAR/STR export
            </p>
          </div>
          <div className="flex items-center gap-2" style={{ minWidth: 360 }}>
            <div className="relative flex-1">
              <Search size={15} style={{ color: '#5A7A9A' }} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={quickAddress}
                onChange={(e) => setQuickAddress(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && goLookup()}
                placeholder="Paste a wallet address to screen it…"
                className="w-full pl-9 pr-3 py-2.5 rounded-lg outline-none"
                style={{ background: '#0C1118', border: '1.5px solid #1E2B3F', color: '#F2F6FC', fontSize: 13 }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#00C8FF'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#1E2B3F'; }}
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={goLookup}
              disabled={!quickAddress.trim()}
              className="px-5 py-2.5 rounded-lg font-bold tracking-wide transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF44', fontSize: 12 }}
            >
              SCREEN
            </motion.button>
          </div>
        </div>
      </div>

      {statsQuery.isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin" size={24} style={{ color: '#00C8FF' }} />
        </div>
      ) : statsQuery.isError ? (
        <div style={{ background: '#0C1118', border: '1px solid #FF1F1F30', borderRadius: 12, padding: 24 }}>
          <p style={{ color: '#FF1F1F', fontSize: 13 }}>Failed to load stats.</p>
        </div>
      ) : stats && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Database} label="TOTAL WALLET TAGS" value={stats.total} color="#00C8FF" delay={0} />
            <StatCard icon={FileWarning} label="RISK CATEGORIES TRACKED" value={Object.keys(stats.by_category).length} color="#FF1F1F" delay={0.05} />
            <StatCard icon={ShieldCheck} label="VERIFICATION PIPELINES" value={sourceCount} color="#F0B429" delay={0.1} />
            <StatCard icon={Wallet} label="ACTIVE / CLEARED TAGS" value={stats.by_status.clear ?? 0} color="#00C853" delay={0.15} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            {/* Top categories — now clickable */}
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}
              style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 14, padding: '20px 22px' }}
            >
              <div className="flex items-center justify-between mb-1">
                <p style={{ color: '#7C8CA6', fontSize: 11.5, letterSpacing: '0.08em', fontWeight: 700 }}>TOP RISK CATEGORIES</p>
                <p style={{ color: '#3A5A7A', fontSize: 10.5 }}>click a category to explore</p>
              </div>
              <div className="mt-3">
                {topCategories.map(([cat, count], i) => {
                  const c = CATEGORY_COLOR[cat] ?? '#5A7A9A';
                  const pct = (count / maxCategory) * 100;
                  return (
                    <motion.button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      whileHover={{ x: 3 }}
                      className="flex items-center gap-3 mb-3.5 w-full text-left group"
                    >
                      <span style={{ color: '#DCE8F5', fontSize: 13, width: 168, flexShrink: 0, fontWeight: 500 }} className="group-hover:underline">
                        {CATEGORY_LABEL[cat] ?? cat}
                      </span>
                      <div style={{ flex: 1, background: '#0F1928', borderRadius: 5, height: 10, overflow: 'hidden' }}>
                        <motion.div
                          initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.15 + i * 0.06 }}
                          style={{ height: 10, borderRadius: 5, background: c, boxShadow: `0 0 8px ${c}66` }}
                        />
                      </div>
                      <span style={{ color: c, fontWeight: 700, fontSize: 12.5, width: 74, textAlign: 'right' }}>
                        {count.toLocaleString()}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>

            {/* Data confidence — sources hidden by default, aggregate framing */}
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.15 }}
              style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 14, padding: '20px 22px' }}
            >
              <p style={{ color: '#7C8CA6', fontSize: 11.5, letterSpacing: '0.08em', fontWeight: 700, marginBottom: 16 }}>DATA CONFIDENCE</p>

              <div className="flex items-center gap-3 mb-4">
                <div style={{ width: 40, height: 40, borderRadius: 11, background: '#00C85318', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Microscope size={18} style={{ color: '#00C853' }} />
                </div>
                <p style={{ color: '#DCE8F5', fontSize: 13, lineHeight: 1.6 }}>
                  Every tag is corroborated across <strong>{sourceCount} independent verification pipelines</strong> — spanning
                  official government sanctions data, peer-reviewed academic research, and direct analyst citation — and graded
                  for source reliability using an Admiralty-Code-style methodology.
                </p>
              </div>

              <div className="flex items-center gap-3 mb-5">
                <div style={{ width: 40, height: 40, borderRadius: 11, background: '#2979FF18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Clock size={18} style={{ color: '#2979FF' }} />
                </div>
                <p style={{ color: '#8D96AC', fontSize: 12.5 }}>
                  Most recently refreshed <strong style={{ color: '#DCE8F5' }}>{relativeTime(mostRecentUpdate)}</strong>
                </p>
              </div>

              <button
                onClick={() => setShowSourceDetail((v) => !v)}
                className="flex items-center gap-1.5"
                style={{ color: '#5A7A9A', fontSize: 11.5 }}
              >
                <motion.span animate={{ rotate: showSourceDetail ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown size={13} />
                </motion.span>
                {showSourceDetail ? 'Hide technical source breakdown' : 'Show technical source breakdown'}
              </button>

              <AnimatePresence>
                {showSourceDetail && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div className="mt-3 flex flex-col gap-2">
                      {Object.entries(stats.by_source).map(([source, count]) => (
                        <div key={source} className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid #0F1520' }}>
                          <span style={{ color: '#8D96AC', fontSize: 11.5 }}>{SOURCE_LABEL[source] ?? source}</span>
                          <span style={{ color: '#5A7A9A', fontSize: 10.5 }}>
                            {count.toLocaleString()} · {relativeTime(stats.freshness_by_source[source])}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p style={{ color: '#3A5A7A', fontSize: 10, marginTop: 10, lineHeight: 1.5 }}>
                      Full per-source citation and reliability grading is always included in generated SAR/STR reports, regardless of this toggle.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* Quick actions */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }}>
            <p style={{ color: '#7C8CA6', fontSize: 11.5, letterSpacing: '0.08em', fontWeight: 700, marginBottom: 12 }}>QUICK ACTIONS</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <QuickAction icon={Search} title="Lookup / Screen Wallet" desc="Check one address — blocklist, sanctions, exposure" color="#00C8FF" onClick={() => navigate('/app/wallet-intel/lookup')} />
              <QuickAction icon={Layers} title="Batch Screen" desc="Screen up to 100 addresses at once" color="#F0B429" onClick={() => navigate('/app/wallet-intel/batch')} />
              <QuickAction icon={FileWarning} title="Generate SAR/STR" desc="FATF/FIU-IND regulatory export" color="#FF6B1A" onClick={() => navigate('/app/wallet-intel/reports')} />
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}
